// src/components/ui/modalStack.js
//
// ONE stack of the modals currently open, so that Escape (and WizardModal's Tab trap) belong to the TOP one
// only. Before it, every AdminModal listened for Escape on `window`, so Esc in a NESTED dialog (the document
// picker's "replace the file?" confirmation inside Gestisci, or inside a persona modal) closed the dialog AND
// the modal behind it in the same keystroke.
//
// ⚠️ Order = the order in which modals OPENED, taken at RENDER time (an order number drawn on the first render
// with `open` true), not when the effect registers it. Effects run child-first, renders parent-first: a modal
// and a dialog nested in it that open in the very same commit (a host mounted with its confirmation already
// open) would otherwise put the OUTER one on top. The stack is kept sorted by that number.
import { useCallback, useEffect, useRef } from 'react';

let stack = [];
let seq = 0;

/** A fresh, increasing order number (later = higher in the stack). */
export function nextModalOrder() {
  seq += 1;
  return seq;
}

export function openModal(order = nextModalOrder()) {
  stack = [...stack, order].sort((a, b) => a - b);
  return order;
}

export function closeModal(id) {
  stack = stack.filter((x) => x !== id);
}

export function isTopModal(id) {
  return stack.length > 0 && stack[stack.length - 1] === id;
}

/** Test helper: forget every registration. */
export function __resetModalStackForTests() {
  stack = [];
}

/**
 * Registers the calling modal while `open`; returns a STABLE `isTop()` to call from key handlers.
 * Both handlers of a nested pair run on the same keydown, BEFORE any re-render, so the inner one is still on
 * top for both — only the inner one acts.
 */
export function useModalLayer(open) {
  const orderRef = useRef(null);
  const idRef = useRef(null);
  // Drawn during render (parent-first); a re-open draws a new, higher number.
  if (open && orderRef.current == null) orderRef.current = nextModalOrder();
  if (!open && orderRef.current != null) orderRef.current = null;
  useEffect(() => {
    if (!open) return undefined;
    const id = openModal(orderRef.current ?? nextModalOrder());
    idRef.current = id;
    return () => {
      closeModal(id);
      idRef.current = null;
    };
  }, [open]);
  return useCallback(() => idRef.current != null && isTopModal(idRef.current), []);
}
